import { getCopilotChatDrawerState } from '../store/store';
import { logCopilotChatSidePaneTelemetryEvent } from 'owa-copilot-telemetry/lib/copilotChatSidePaneTelemetryLogger';
import { setUserPreferredWidthMutator } from '../mutators/setUserPreferredWidthMutator';
import { onCopilotChatDrawerLayoutChange } from '../actions/onCopilotChatDrawerLayoutChange';
import { onCopilotChatDrawerCollapsedState } from '../actions/onCopilotChatDrawerCollapsedState';
import { onCopilotChatDrawerExpandedState } from '../actions/onCopilotChatDrawerExpandedState';
import {
    MINIMUM_COPILOT_CHAT_DRAWER_WIDTH,
    EXPANDED_COPILOT_CHAT_DRAWER_WIDTH_CANVAS_RATIO,
} from '../utils/copilotChatDrawerConstants';
import { getShowBizChatInReadingPane } from '../selectors/getShowBizChatInReadingPane';
import { closeBizChatInReadingPaneAndSetWidthMutator } from '../mutators/closeBizChatInReadingPaneAndSetWidthMutator';
import type { MailboxInfo } from 'owa-client-types';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

/**
 * Function to handle the expand/collapse button click for Copilot Chat Drawer.
 * @param targetWindow The target window
 * Will expand to 50% of canvas width when expanding, and collapse to minimum width when collapsing.
 * @param isExpanding Whether Chat Drawer is expanding or collapsing
 * @param mailboxInfo MailboxInfo for the account. If not provided, falls back to getModuleContextMailboxInfo().
 */
export function toggleCopilotChatDrawerExpandCollapse(
    targetWindow: Window,
    isExpanding: boolean,
    mailboxInfo?: MailboxInfo
): void {
    const state = getCopilotChatDrawerState(targetWindow);
    if (!state) {
        return; // No state means drawer was never opened
    }
    const newWidth = isExpanding
        ? state.appCanvasWidth * EXPANDED_COPILOT_CHAT_DRAWER_WIDTH_CANVAS_RATIO
        : MINIMUM_COPILOT_CHAT_DRAWER_WIDTH;

    if (!isExpanding && getShowBizChatInReadingPane(targetWindow)) {
        closeBizChatInReadingPaneAndSetWidthMutator(targetWindow, newWidth);
    } else {
        setUserPreferredWidthMutator(targetWindow, newWidth);
    }

    logCopilotChatSidePaneTelemetryEvent(
        'CopilotSidePane_ExpandCollapseClicked',
        {
            IsExpanding: isExpanding,
        },
        mailboxInfo
    );

    // If user toggles collapse, send signal out.
    // Used for listeners that need to know this signal, e.g. whether we need to reopen folder pane.
    if (isGlobalFeatureEnabled('mon-copilot-bizchat-drawer-leftnav-collapse')) {
        if (isExpanding) {
            onCopilotChatDrawerExpandedState(targetWindow);
        } else {
            onCopilotChatDrawerCollapsedState(targetWindow);
        }
    }

    if (targetWindow === window) {
        onCopilotChatDrawerLayoutChange();
    }
}
